import { cachedPhotos, invalidatePhotos, setPhotoSession, type DownloadPhoto } from "./photo-cache";

const API_BASE_URL = import.meta.env.VITE_WINEPS_API_URL ?? "https://api.bonvino.fr";

export interface User {
  id: string;
  email: string;
  display_name: string;
  username: string;
  idwineps: string | null;
  is_admin: boolean;
}

export interface AccountAccess {
  mode: "authentik" | "provider_managed" | "unavailable";
  provider: "authentik" | "google" | "unknown";
  message: string;
  email_action_url: string | null;
  password_action_url: string | null;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  display_name: string;
  status: "active" | "inactive";
  last_authenticated_at: string | null;
  tasting_count: number;
  created_at: string;
  invitation_quota: number;
}

export interface Invitation {
  id: string;
  email: string;
  status: "active" | "consommée" | "expirée" | "annulée";
  created_at: string;
  expires_at: string;
  last_sent_at: string;
  invitation_url: string | null;
}

export interface PersonalInvitation extends Invitation {
  first_name: string | null;
}

export interface PersonalInvitations {
  quota: number;
  items: PersonalInvitation[];
}

export interface FeedbackResult { id: string; version: string; message: string; }

export interface AuditEvent {
  id: string;
  created_at: string;
  actor_user_id: string;
  action: string;
  target_type: string;
  target_id: string;
  result: "success" | "failure";
  metadata: Record<string, unknown>;
}

export interface KpiDashboard {
  timezone: string;
  as_of: string;
  kpis: Array<{ id: string; label: string; value: number; period: string }>;
}

export interface CandidateReference {
  cuvee_id: string;
  domain_id: string | null;
  domain: string | null;
  wine: string;
  status: "candidate" | "official" | "merged";
  confirmation_count: number;
  provenance: string[];
  created_at: string;
  updated_at: string;
}

export interface ReferenceImportPreview {
  attempt_id?: string;
  preview_id: string | null;
  fingerprint: string;
  expires_at: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  official_rows: number;
  candidate_rows: number;
  confirmable: boolean;
  rows: Array<{
    line: number;
    domaine: string;
    wine: string;
    couleur: string | null;
    cepage: string | null;
    region: string | null;
    aop: string | null;
    sparkling: boolean;
    status: "official" | "candidate";
  }>;
  errors: Array<{
    line: number;
    column: string;
    code: string;
    message: string;
    value: string;
  }>;
}

export interface ReferenceImportSummary {
  preview_id: string;
  created: number;
  official: number;
  candidate: number;
}

export interface ReferenceImportAttempt {
  id: string;
  actor_id: string;
  filename: string;
  created_at: string;
  updated_at: string;
  preview_id: string | null;
  result: "pending" | "previewed" | "rejected" | "confirmed" | "failed" | "expired" | "conflict";
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  created_rows: number;
  official_rows: number;
  candidate_rows: number;
  diagnostics: Array<{ line: number; code: string; column: string; message: string }>;
}

export interface WineMatchingSettings {
  reference_search_enabled: boolean;
  grapeminds_enabled: boolean;
  external_timeout_seconds: number;
  preselection_threshold: number;
  proposal_threshold: number;
  domain_weight: number;
  wine_weight: number;
  updated_at?: string;
  updated_by_user_id?: string;
}

export interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface Preferences {
  onboarding_status: "pending" | "ignored" | "completed";
  initial_taste_text: string;
  rating_scale: "stars" | "twenty";
  product_consent?: boolean | null;
}

export interface TasteProfile {
  version: number;
  based_on_tasting_count: number;
  generated_at: string;
  summary: string;
  notification_unread: boolean;
  structured: {
    general_preferences: string;
    appreciated: string[];
    disliked: string[];
    budget: string;
    habits_and_occasions: string;
    discovery_expectations: string;
    inferred_trends: string[];
    confidence: string;
    data_count: number;
  };
}

export interface TasteProfileStatus {
  profile: TasteProfile | null;
  generation_threshold: number;
  regeneration_interval: number;
}

export interface Recipient {
  id: string;
  display_name: string;
  username: string;
}

export type WineColor = "red" | "white" | "rose" | "sparkling" | "other";

export interface Tasting {
  id: string;
  owner_user_id: string;
  date: string;
  wine_name: string;
  producer: string | null;
  appellation: string | null;
  vintage: number | null;
  country: string | null;
  region: string | null;
  grape: string | null;
  grape_varieties: string[];
  color: WineColor | null;
  rating: number | null;
  notes: string | null;
  source_cellar_entry_id?: string;
  wine_cuvee_id?: string;
  sparkling?: boolean | null;
  cellar_stock?: { quantity: number; status: "available" | "exhausted"; label: string; source_identity_changed?: boolean; source_version?: number } | null;
  shared?: boolean;
}

export interface TastingFilters {
  query?: string;
  source_cellar_entry_id?: string;
  cuvee_id?: string;
  from_cellar?: boolean;
  year?: number;
  grape?: string;
  rating_min?: number;
  rating_max?: number;
}

export interface TastingInput {
  source_cellar_entry_id?: string;
  source_version?: number;
  wine_name: string;
  producer?: string;
  appellation?: string;
  vintage?: number;
  country?: string;
  region?: string;
  grape?: string;
  grape_varieties?: string[];
  color?: WineColor;
  sparkling?: boolean;
  rating?: number;
  notes?: string;
  tasting_date?: string;
}

export interface AOPDiscovery {
  name: string;
  region: string;
  kind: "proche" | "originale";
  explanation: string;
  wine_types: string[];
  main_grapes: string[];
  styles: string[];
  aromas: string[];
  body: string;
  acidity: string;
  tannins: string;
  sweetness: string;
  price_range_eur: [number, number];
  distinctive_traits: string;
}

export interface AOPDiscoveryResponse {
  reference_version: string;
  items: AOPDiscovery[];
}

export interface TastingPage {
  items: Tasting[];
  page: number;
  page_size: number;
  has_more: boolean;
  total: number;
}

export interface PresignedUpload {
  photo_id: string;
  object_key: string;
  upload_url: string;
  content_type?: string;
  role: "label" | "back_label";
}

export interface TastingPhoto {
  id: string;
  url: string;
  role: "label" | "back_label";
}

export interface LabelAnalysis {
  wine_name: string | null;
  producer: string | null;
  vintage: number | null;
  country: string | null;
  region: string | null;
  appellation: string | null;
  grape: string | null;
  grape_varieties: string[];
  confidence: number;
  color: WineColor | null;
}

export interface WineResolutionProposal {
  source: "wineps" | "grapeminds";
  score: number;
  official: boolean;
  domain_id?: string;
  domain: string | null;
  cuvee_id?: string;
  wine: string;
  appellation?: string | null;
  region?: string | null;
  grape?: string | null;
  grape_varieties?: string[];
  color?: WineColor | null;
  sparkling?: boolean | null;
  status?: "candidate" | "official" | "merged";
  external_id?: string | null;
}

export interface WineResolution {
  resolution_id: string;
  expires_at: string;
  schema_version: 2;
  extracted: { domain: string | null; wine: string | null };
  selected: WineResolutionProposal | null;
  proposals: WineResolutionProposal[];
  external_status: string;
}

export type AgentMessageRole = "user" | "assistant";
export type AgentMessageStatus = "processing" | "completed" | "failed";
export type AgentConversationStatus = "active" | "archived";

export interface AgentAttachment {
  id: string;
  media_type: string;
  size_bytes: number;
  page_count: number | null;
  status: AgentMessageStatus;
}

export interface AgentMessage {
  id: string;
  role: AgentMessageRole;
  text: string;
  status: AgentMessageStatus;
  created_at: string;
  attachment: AgentAttachment | null;
  failure_reason?: string | null;
}

export interface AgentConversation {
  id: string;
  conversation_date: string;
  timezone: "Europe/Paris";
  status: AgentConversationStatus;
  created_at: string;
  last_message_at: string | null;
  message_count: number;
  has_attachments?: boolean;
}

export interface AgentConversationDetail {
  conversation: AgentConversation;
  messages: AgentMessage[];
  page?: number;
  page_size?: number;
  has_more?: boolean;
}

export interface AgentSendResult {
  conversation_id: string;
  user_message: AgentMessage;
  assistant_message: AgentMessage | null;
}

export interface CellarEntry {
  id: string;
  identity_locked?: boolean;
  wine_name: string;
  producer: string | null;
  appellation: string | null;
  country: string | null;
  region: string | null;
  grape: string | null;
  grape_varieties: string[];
  vintages_text: string | null;
  color: WineColor;
  sparkling: boolean | null;
  base_color_required: boolean;
  quantity: number;
  location: string | null;
  version: number;
  wine_cuvee_id: string | null;
  wine_reference_source: string | null;
  created_at: string | null;
}

export interface CellarPhoto {
  id: string;
  cellar_entry_id: string;
  content_type: string;
  file_size: number;
  checksum: string;
  role: "label" | "back_label";
}

export interface CellarOptimization {
  id: string;
  summary: string;
  strengths: string[];
  gaps: string[];
  recommendations: Array<{ id: string; reference_id: string; title: string; explanation: string; interested: boolean }>;
}

export class APIError extends Error {
  constructor(readonly status: number, message: string, readonly code: string | null = null, readonly details: Record<string, unknown> | null = null) {
    super(message);
  }
}

export class NetworkError extends Error {
  constructor() {
    super("La connexion à WinePS a été interrompue. Vérifier mon réseau puis réessayer.");
  }
}

/** Translate transport failures without retrying writes or OAuth exchanges. */
export async function networkFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try { return await fetch(input, init); }
  catch (reason) {
    if (init?.signal?.aborted || (reason instanceof DOMException && reason.name === "AbortError")) throw reason;
    throw new NetworkError();
  }
}

const pendingReads = new Map<string, Promise<unknown>>();

export function resetApiSession(): void {
  pendingReads.clear();
  setPhotoSession(null);
}

function retryDelay(response: Response): number | null {
  const value = response.headers.get("Retry-After");
  if (!value) return null;
  const seconds = /^\d+$/.test(value) ? Number(value) : (Date.parse(value) - Date.now()) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 && seconds <= 5 ? seconds * 1000 : null;
}

async function pause(delay: number, signal?: AbortSignal | null): Promise<void> {
  signal?.throwIfAborted();
  await new Promise<void>((resolve, reject) => {
    const abort = () => { window.clearTimeout(timer); reject(signal?.reason); };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, delay);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

async function performRequest<T>(
  path: string, accessToken: string, init: RequestInit,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);
  if (!headers.has("X-WinePS-Contract")) headers.set("X-WinePS-Contract", "2");
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const read = (init.method ?? "GET").toUpperCase() === "GET";
  for (let attempt = 0; ; attempt += 1) {
    let response: Response;
    try {
      response = await networkFetch(`${API_BASE_URL}${path}`, { ...init, headers });
    } catch (reason) {
      if (init.signal?.aborted || (reason instanceof DOMException && reason.name === "AbortError")) throw reason;
      if (read && attempt === 0) {
        await pause(200 + Math.random() * 200, init.signal);
        continue;
      }
      throw new NetworkError();
    }
    if (!response.ok) {
      if (response.status === 401) resetApiSession();
      const delay = response.status === 429 ? retryDelay(response) : null;
      if (read && attempt === 0 && delay !== null) {
        await response.body?.cancel();
        await pause(delay + Math.random() * 100, init.signal);
        continue;
      }
      const body = await response.json().catch(() => null);
      throw new APIError(
        response.status,
        response.status === 429
          ? "Trop de chargements rapprochés. Réessayez dans quelques instants."
          : body?.error?.message ?? "La requête WinePS a échoué.",
        body?.error?.code ?? null,
        body?.error ?? null,
      );
    }
    if (!read && /photos|tastings|cellar\/entries/.test(path)) {
      pendingReads.clear();
      invalidatePhotos();
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}

function request<T>(path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const read = (init.method ?? "GET").toUpperCase() === "GET";
  // Explicit signals retain independent cancellation semantics.
  if (!read || init.signal) return performRequest<T>(path, accessToken, init);
  const key = JSON.stringify([accessToken, path, Array.from(new Headers(init.headers))]);
  const existing = pendingReads.get(key);
  if (existing) return existing as Promise<T>;
  const pending = performRequest<T>(path, accessToken, init);
  pendingReads.set(key, pending);
  const cleanup = () => { if (pendingReads.get(key) === pending) pendingReads.delete(key); };
  void pending.then(cleanup, cleanup);
  return pending;
}

export const winepsApi = {
  importHistory: (token: string, page = 1) => request<Page<ReferenceImportAttempt> & { total: number }>(`/api/admin/wine-references/imports?page=${page}&page_size=20`, token),
  importAttempt: (token: string, id: string) => request<ReferenceImportAttempt>(`/api/admin/wine-references/imports/${id}`, token),
  importReport: async (token: string, id: string) => {
    const response = await networkFetch(`${API_BASE_URL}/api/admin/wine-references/imports/${id}/report`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new APIError(response.status, "Le rapport d’import est indisponible.");
    return response.text();
  },
  me: (token: string) => request<User>("/api/me", token),
  accountAccess: (token: string) => request<AccountAccess>("/api/account-access", token),
  chooseIdentity: (token: string, idwineps: string) => request<User>("/api/identity", token, {
    method: "POST", body: JSON.stringify({ idwineps }),
  }),
  updateDisplayName: (token: string, display_name: string) => request<User>("/api/profile/display-name", token, {
    method: "PUT", body: JSON.stringify({ display_name }),
  }),
  adminUsers: (token: string, query = "") =>
    request<Page<AdminUser>>(`/api/admin/users?page_size=100&search=${encodeURIComponent(query)}`, token),
  adminKpis: (token: string) => request<KpiDashboard>("/api/admin/kpis", token),
  adminKpiExport: async (token: string) => {
    const response = await networkFetch(`${API_BASE_URL}/api/admin/kpis/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new APIError(response.status, "L’export KPI est indisponible.");
    return response.text();
  },
  adminUser: (token: string, id: string) =>
    request<AdminUser>(`/api/admin/users/${id}`, token),
  setAdminUserStatus: (token: string, id: string, status: AdminUser["status"]) =>
    request<AdminUser>(`/api/admin/users/${id}/status`, token, {
      method: "PATCH", body: JSON.stringify({ status }),
    }),
  setAdminUserQuota: (token: string, id: string, invitation_quota: number) =>
    request<AdminUser>(`/api/admin/users/${id}/invitation-quota`, token, {
      method: "PATCH", body: JSON.stringify({ invitation_quota }),
    }),
  adminInvitations: (token: string) =>
    request<Page<Invitation>>("/api/admin/invitations?page_size=100", token),
  createInvitation: (token: string, email: string) =>
    request<Invitation>("/api/admin/invitations", token, {
      method: "POST", body: JSON.stringify({ email }),
    }),
  resendInvitation: (token: string, id: string) =>
    request<Invitation>(`/api/admin/invitations/${id}/resend`, token, { method: "POST" }),
  revokeInvitation: (token: string, id: string) =>
    request<Invitation>(`/api/admin/invitations/${id}/revoke`, token, { method: "POST" }),
  personalInvitations: (token: string) => request<PersonalInvitations>("/api/invitations", token),
  releaseVersion: (token: string) => request<{ version: string | null }>("/api/release-version", token),
  submitFeedback: (token: string, category: "dégustation" | "cave" | "assistant" | "mon compte" | "autre", comment: string, contact_consent: boolean) => request<FeedbackResult>("/api/feedback", token, { method: "POST", body: JSON.stringify({ category, comment, contact_consent }) }),
  createPersonalInvitation: (token: string, first_name: string, email: string) =>
    request<PersonalInvitation>("/api/invitations", token, {
      method: "POST", body: JSON.stringify({ first_name, email }),
    }),
  resendPersonalInvitation: (token: string, id: string) =>
    request<PersonalInvitation>(`/api/invitations/${id}/resend`, token, { method: "POST" }),
  adminAudit: (token: string, action = "", result = "") => {
    const parameters = new URLSearchParams({ page_size: "100" });
    if (action) parameters.set("action", action);
    if (result) parameters.set("result", result);
    return request<Page<AuditEvent>>(`/api/admin/audit-events?${parameters}`, token);
  },
  adminCandidates: (token: string, page = 1) =>
    request<Page<CandidateReference>>(`/api/admin/wine-references/candidates?page=${page}&page_size=20`, token),
  adminCandidate: (token: string, id: string) =>
    request<CandidateReference>(`/api/admin/wine-references/candidates/${id}`, token),
  adminOfficial: (token: string, page = 1) =>
    request<Page<CandidateReference>>(`/api/admin/wine-references/official?page=${page}&page_size=20`, token),
  previewReferenceImport: (token: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<ReferenceImportPreview>("/api/admin/wine-references/imports/preview", token, {
      method: "POST", body,
    });
  },
  confirmReferenceImport: (token: string, previewId: string) =>
    request<ReferenceImportSummary>(`/api/admin/wine-references/imports/${previewId}/confirm`, token, {
      method: "POST",
    }),
  adminReferenceAudit: (token: string, page = 1, action = "") => {
    const parameters = new URLSearchParams({ page: String(page), page_size: "20" });
    if (action) parameters.set("action", action);
    return request<Page<AuditEvent>>(`/api/admin/wine-references/audit-events?${parameters}`, token);
  },
  adminReferenceAuditEvent: (token: string, id: string) =>
    request<AuditEvent>(`/api/admin/wine-references/audit-events/${id}`, token),
  updateAdminCandidate: (token: string, id: string, body: { domain: string; wine: string }) =>
    request<CandidateReference>(`/api/admin/wine-references/candidates/${id}`, token, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  finalizeAdminCandidate: (token: string, id: string, body: { domain: string; wine: string }) =>
    request<{ cuvee_id: string; status: string }>(`/api/admin/wine-references/candidates/${id}/finalize`, token, {
      method: "POST", body: JSON.stringify(body),
    }),
  mergeAdminCandidate: (token: string, id: string, target_cuvee_id: string) =>
    request<{ source_cuvee_id: string; target_cuvee_id: string; status: string }>(`/api/admin/wine-references/candidates/${id}/merge`, token, {
      method: "POST", body: JSON.stringify({ target_cuvee_id }),
    }),
  matchingSettings: (token: string) =>
    request<WineMatchingSettings>("/api/admin/wine-matching/settings", token),
  updateMatchingSettings: (token: string, values: WineMatchingSettings) =>
    request<WineMatchingSettings>("/api/admin/wine-matching/settings", token, {
      method: "PUT", body: JSON.stringify({
        reference_search_enabled: values.reference_search_enabled,
        grapeminds_enabled: values.grapeminds_enabled,
        external_timeout_seconds: values.external_timeout_seconds,
        preselection_threshold: values.preselection_threshold,
        proposal_threshold: values.proposal_threshold,
        domain_weight: values.domain_weight,
        wine_weight: values.wine_weight,
      }),
    }),
  preferences: (token: string) => request<Preferences>("/api/preferences", token),
  tasteProfile: (token: string) =>
    request<TasteProfileStatus>("/api/taste-profile", token),
  acknowledgeTasteProfile: (token: string) =>
    request<TasteProfile | null>("/api/taste-profile/acknowledge", token, {
      method: "POST",
    }),
  previewOnboarding: (token: string, answers: string[]) =>
    request<{ text: string }>("/api/onboarding/preview", token, {
      method: "POST",
      body: JSON.stringify({ answers }),
    }),
  completeOnboarding: (token: string, text: string) =>
    request<Preferences>("/api/onboarding/complete", token, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  ignoreOnboarding: (token: string) =>
    request<Preferences>("/api/onboarding/ignore", token, { method: "POST" }),
  reopenOnboarding: (token: string) =>
    request<Preferences>("/api/onboarding/reopen", token, { method: "POST" }),
  previewProfile: (token: string, text: string) =>
    request<{ text: string }>("/api/profile/preview", token, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  confirmProfile: (token: string, text: string) =>
    request<Preferences>("/api/profile/text", token, {
      method: "PUT",
      body: JSON.stringify({ text }),
    }),
  setRatingScale: (token: string, rating_scale: Preferences["rating_scale"]) =>
    request<Preferences>("/api/preferences/rating-scale", token, {
      method: "PATCH",
      body: JSON.stringify({ rating_scale }),
    }),
  discoverAOPs: (token: string) =>
    request<AOPDiscoveryResponse>("/api/discoveries/aop", token, { method: "POST" }),
  activeAgentConversation: (token: string) =>
    request<AgentConversationDetail>("/api/agent/conversations/active", token),
  agentConversations: (token: string, page = 1, pageSize = 20) =>
    request<Page<AgentConversation>>(`/api/agent/conversations?page=${page}&page_size=${pageSize}`, token),
  agentConversation: (token: string, conversationId: string, page = 1, pageSize = 50) =>
    request<AgentConversationDetail>(
      `/api/agent/conversations/${conversationId}?page=${page}&page_size=${pageSize}`,
      token,
    ),
  sendAgentMessage: (token: string, message: string, idempotencyKey: string) =>
    request<AgentSendResult>("/api/agent/messages", token, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ message }),
    }),
  sendAgentAttachment: (token: string, message: string, attachment: File, idempotencyKey: string) => {
    const body = new FormData();
    body.set("message", message);
    body.set("attachment", attachment);
    return request<AgentSendResult>("/api/agent/messages", token, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body,
    });
  },
  agentMessage: (token: string, messageId: string) =>
    request<AgentMessage>(`/api/agent/messages/${messageId}`, token),
  agentAttachmentUrl: (token: string, attachmentId: string) =>
    request<{ url: string; expires_at: string }>(`/api/agent/attachments/${attachmentId}/url`, token),
  tastingFilterOptions: (token: string) =>
    request<{ years: number[]; grapes: string[] }>("/api/tastings/filter-options", token),
  list: (token: string, page = 1, filters: TastingFilters = {}) => {
    const parameters = new URLSearchParams({ page: String(page), page_size: "20" });
    if (filters.query !== undefined) parameters.set("query", filters.query);
    if (filters.source_cellar_entry_id) parameters.set("source_cellar_entry_id", filters.source_cellar_entry_id);
    if (filters.cuvee_id) parameters.set("cuvee_id", filters.cuvee_id);
    if (filters.from_cellar) parameters.set("from_cellar", "true");
    if (filters.year !== undefined) parameters.set("year", String(filters.year));
    if (filters.grape) parameters.set("grape", filters.grape);
    if (filters.rating_min !== undefined) parameters.set("rating_min", String(filters.rating_min));
    if (filters.rating_max !== undefined) parameters.set("rating_max", String(filters.rating_max));
    return request<TastingPage>(`/api/tastings?${parameters}`, token);
  },
  cellarSummary: (token: string) => request<{ references: number; bottles: number }>("/api/cellar/summary", token),
  cellarEntries: (token: string, params = "") => request<Page<CellarEntry> & { total?: number }>(`/api/cellar/entries?${params}`, token),
  cellarEntry: (token: string, id: string) => request<CellarEntry>(`/api/cellar/entries/${id}`, token),
  cellarPhotos: (token: string, entryId: string) => request<CellarPhoto[]>(`/api/cellar/entries/${entryId}/photos`, token),
  cellarDownloadPhotos: (token: string, entryId: string, force = false): Promise<DownloadPhoto[]> =>
    cachedPhotos(token, `cellar:${entryId}`, () => request<DownloadPhoto[]>(
      `/api/cellar/entries/${entryId}/photos?include_download_urls=true`, token,
    ), force),
  cellarPhotoUrl: (token: string, entryId: string, photoId: string) => request<{ url: string }>(`/api/cellar/entries/${entryId}/photos/${photoId}/url`, token),
  cellarDuplicateCheck: (token: string, body: { wine_name: string; producer?: string; vintages_text?: string; wine_cuvee_id?: string }) =>
    request<{ entry: CellarEntry | null }>("/api/cellar/duplicate-check", token, { method: "POST", body: JSON.stringify(body) }),
  createCellarEntry: (token: string, body: Record<string, unknown>) => request<CellarEntry>("/api/cellar/entries", token, { method: "POST", headers: { "X-WinePS-Contract": "3" }, body: JSON.stringify(body) }),
  cellarPresign: (token: string, entryId: string, filename: string, role: "label" | "back_label" = "label") => request<{ photo_id: string; object_key: string; upload_url: string; content_type: string }>("/api/cellar/uploads/presign", token, { method: "POST", body: JSON.stringify({ cellar_entry_id: entryId, filename, role }) }),
  confirmCellarPhoto: (token: string, entryId: string, photoId: string, objectKey: string, role: "label" | "back_label" = "label") => request<{ id: string }>("/api/cellar/photos/confirm", token, { method: "POST", body: JSON.stringify({ cellar_entry_id: entryId, photo_id: photoId, object_key: objectKey, role }) }),
  updateCellarQuantity: (token: string, id: string, quantity: number, version: number, confirmZero = false) => request<CellarEntry>(`/api/cellar/entries/${id}`, token, { method: "PATCH", body: JSON.stringify({ quantity, version, confirm_zero: confirmZero }) }),
  updateCellarEntry: (token: string, id: string, body: Record<string, unknown>) => request<CellarEntry>(`/api/cellar/entries/${id}`, token, { method: "PUT", headers: { "X-WinePS-Contract": "3" }, body: JSON.stringify(body) }),
  consumeCellarEntry: (token: string, id: string, tastingId: string, confirmZero = false, sourceVersion?: number, confirmIdentityChanged = false) => request<CellarEntry>(`/api/cellar/entries/${id}/consume`, token, { method: "POST", body: JSON.stringify({ tasting_id: tastingId, confirm_zero: confirmZero, source_version: sourceVersion, confirm_source_identity_changed: confirmIdentityChanged }) }),
  optimizationEligibility: (token: string) => request<{ eligible: boolean; profile_available: boolean; references: number; minimum_references: number }>("/api/cellar/optimization-eligibility", token),
  optimizeCellar: (token: string) => request<CellarOptimization>("/api/cellar/optimizations", token, { method: "POST" }),
  latestOptimization: (token: string) => request<CellarOptimization | null>("/api/cellar/optimizations/latest", token),
  setOptimizationInterest: (token: string, runId: string, recommendationId: string, interested: boolean) => request<CellarOptimization>(`/api/cellar/optimizations/${runId}/recommendations/${recommendationId}/interest`, token, { method: "PUT", body: JSON.stringify({ interested }) }),
  productConsent: (token: string) => request<{ consent: boolean | null; prompt_due: boolean }>("/api/product-consent", token),
  setProductConsent: (token: string, consent: boolean) => request<{ consent: boolean; prompt_due: boolean }>("/api/product-consent", token, { method: "PUT", body: JSON.stringify({ consent }) }),
  recordProductEvent: (token: string, eventType: string, properties: Record<string, string | number | boolean> = {}) =>
    request<void>("/api/product-events", token, {
      method: "POST",
      body: JSON.stringify({ event_type: eventType, user_story: "BL-074", version: "sprint-49", properties }),
    }),
  shared: (token: string, page = 1, query = "") =>
    request<TastingPage>(
      `/api/tastings/shared?page=${page}&page_size=20&query=${encodeURIComponent(query)}`,
      token,
    ).then((result) => ({
      ...result,
      items: result.items.map((item) => ({ ...item, shared: true })),
    })),
  get: (token: string, id: string) => request<Tasting>(`/api/tastings/${id}`, token),
  create: (token: string, body: TastingInput) =>
    request<Tasting>("/api/tastings", token, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  analyzeLabel: (token: string, file: File, backFile?: File | null) => {
    const body = new FormData();
    body.set("image", file);
    if (backFile) body.set("back_image", backFile);
    return request<LabelAnalysis>("/api/labels/analyze", token, {
      method: "POST",
      body,
    });
  },
  resolveWine: (token: string, body: { domain?: string; wine?: string; source_cellar_entry_id?: string; source_version?: number }) =>
    request<WineResolution>("/api/wine-resolutions", token, {
      method: "POST", body: JSON.stringify(body),
    }),
  confirmWineResolution: (
    token: string,
    resolutionId: string,
    body: {
      choice: { kind: "reference" | "none"; cuvee_id?: string };
      domain?: string;
      wine: string;
      vintage?: number;
      appellation?: string;
      country?: string;
      region?: string;
      grape?: string;
      grape_varieties?: string[];
      color?: WineColor;
      sparkling?: boolean;
      source_cellar_entry_id?: string;
      source_version?: number;
      inherit_source_photo?: boolean;
      rating?: number;
      notes?: string;
      tasting_date?: string;
      tasting_id?: string;
    },
    idempotencyKey: string,
  ) => request<Tasting>(`/api/wine-resolutions/${resolutionId}/confirm`, token, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  }),
  update: (token: string, id: string, body: Partial<TastingInput>) =>
    request<Tasting>(`/api/tastings/${id}`, token, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (token: string, id: string) =>
    request<void>(`/api/tastings/${id}`, token, { method: "DELETE" }),
  resolveUser: (token: string, query: string) =>
    request<Recipient>(`/api/users/resolve?query=${encodeURIComponent(query)}`, token),
  share: (token: string, tastingId: string, recipientId: string) =>
    request<{ permission: string }>(`/api/tastings/${tastingId}/shares`, token, {
      method: "POST",
      body: JSON.stringify({ recipient_user_id: recipientId }),
    }),
  presign: (
    token: string,
    tastingId: string,
    filename: string,
    role: "label" | "back_label" = "label",
  ) =>
    request<PresignedUpload>("/api/uploads/presign", token, {
      method: "POST",
      body: JSON.stringify({ tasting_id: tastingId, filename, role }),
    }),
  uploadPhoto: async (
    token: string,
    tastingId: string,
    file: File,
    role: "label" | "back_label" = "label",
  ) => {
    const upload = await winepsApi.presign(token, tastingId, file.name, role);
    const response = await networkFetch(upload.upload_url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!response.ok) throw new APIError(response.status, "L’envoi de la photo a échoué.");
    return request<{ id: string }>("/api/photos/confirm", token, {
      method: "POST",
      body: JSON.stringify({
        tasting_id: tastingId,
        photo_id: upload.photo_id,
        object_key: upload.object_key,
        role,
      }),
    });
  },
  uploadPhotoWithPresigned: async (
    token: string,
    tastingId: string,
    file: File,
    upload: PresignedUpload,
  ) => {
    const response = await networkFetch(upload.upload_url, {
      method: "PUT",
      headers: { "Content-Type": upload.content_type ?? file.type ?? "application/octet-stream" },
      body: file,
    });
    if (!response.ok) throw new APIError(response.status, "L’envoi de la photo a échoué.");
    return request<{ id: string }>("/api/photos/confirm", token, {
      method: "POST",
      body: JSON.stringify({
        tasting_id: tastingId,
        photo_id: upload.photo_id,
        object_key: upload.object_key,
        role: upload.role,
      }),
    });
  },
  photos: (
    token: string,
    tastingId: string,
    variant: "original" | "display" | "thumbnail" = "display",
    force = false,
  ): Promise<DownloadPhoto[]> => cachedPhotos(token, `tasting:${tastingId}:${variant}`, () =>
    request<DownloadPhoto[]>(
      `/api/tastings/${tastingId}/photos?include_download_urls=true&variant=${variant}`, token,
    ), force),
  deletePhoto: (token: string, tastingId: string, photoId: string) =>
    request<void>(`/api/tastings/${tastingId}/photos/${photoId}`, token, {
      method: "DELETE",
    }),
};
